import React from 'react';
import { rsvpMeta, formatRespondedAt, interviewerRoster } from '../utils/rsvpStatus';

// ─────────────────────────────────────────────────────────────────────────────
// RoundRsvp — who has answered the invite for one interview round.
//
// Shared by the Interviews page and the Submissions card because they show the
// identical thing; two copies drifted apart within a day of being written.
// Styled with the global CSS custom properties rather than either page's local
// palette, so it stays theme-aware and renders the same in both places.
//
// Layout is a two-column grid (label / value) rather than a wrapping flex row.
// The flex version broke timestamps mid-value — "6 Aug, 3:50" on one line and
// "pm" on the next — and left the labels ragged, so it read as one run-on list
// where you could not tell which tick belonged to whom.
// ─────────────────────────────────────────────────────────────────────────────

const LABEL = {
  color: 'var(--text-muted)',
  fontSize: 10.5,
  whiteSpace: 'nowrap',
  // Optical alignment: the label sits on the same baseline as the first value
  // even when the value wraps to several lines.
  paddingTop: 0.5,
};

const AWAITING_HINT =
  'No reply yet. Many mail clients — the Gmail app especially — never offer an '
  + 'RSVP button, so silence here does not mean they are not coming.';

export default function RoundRsvp({ round }) {
  // Nothing to report until an invite actually went out — before that there is
  // nothing for anyone to have answered.
  if (!round?.inviteSentAt) return null;

  const meta   = rsvpMeta(round.candidateResponse);
  const at     = formatRespondedAt(round.candidateRespondedAt);
  const roster = interviewerRoster(round);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'max-content minmax(0, 1fr)',
        columnGap: 10,
        rowGap: 3,
        fontSize: 11,
        lineHeight: 1.45,
        alignItems: 'start',
      }}
    >
      <span style={LABEL}>Candidate</span>
      {meta ? (
        <span style={{ color: meta.color, fontWeight: 600, whiteSpace: 'nowrap' }}
          title={at ? `Replied ${at}` : 'Replied'}>
          {meta.icon} {meta.label}
        </span>
      ) : (
        <span style={{ color: 'var(--text-muted)' }} title={AWAITING_HINT}>
          Awaiting reply
        </span>
      )}

      {roster.length > 0 && (
        <>
          <span style={LABEL}>Interviewer{roster.length > 1 ? 's' : ''}</span>
          {/* Row gap 0 keeps wrapped names tight; the column gap does the
              separating, so no punctuation is needed between chips. */}
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', minWidth: 0 }}>
            {roster.map((iv) => {
              const im = rsvpMeta(iv.response);
              return (
                <span
                  key={iv.email}
                  style={{
                    color: im ? im.color : 'var(--text-muted)',
                    fontWeight: im ? 600 : 400,
                    whiteSpace: 'nowrap',
                  }}
                  title={`${iv.email}${iv.at ? ` — replied ${formatRespondedAt(iv.at)}` : ' — no reply yet'}`}
                >
                  {im ? im.icon : '○'} {iv.name}
                  {!im && <span style={{ fontWeight: 400, opacity: 0.75 }}> · awaiting</span>}
                </span>
              );
            })}
          </span>
        </>
      )}
    </div>
  );
}
